/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { EmpJobMappingsDaoService } from '../../dao/emp-job-mapping-dao.service';
import { EmpJobMappingsService } from '../emp-job-mapping.service';

import {
  EmpJobMappingDTO,
  EmpJobMapping,
  EmpJobMappingCombinedDTO,
} from 'src/app/_model/emp-job-mapping';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { TimeUtils } from 'src/app/_util/time-utils';
import { Servicejob } from 'src/app/_model/servicejob';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { App, AppState } from '@capacitor/app';
import { LBSUtils } from 'src/app/_util/lbs-utils';
import { isSameDay } from 'date-fns';
import { Employee } from 'src/app/_model/employee';
import { Car } from 'src/app/_model/car';
import { CarsService } from 'src/app/_services/cars/logic/cars.service';

/**
 * Loads EmpJobMapping
 */
@Injectable({
  providedIn: 'root',
})
export class EmpJobMappingsLogicService implements EmpJobMappingsService {
  // Public Member
  public empJobMappings$ = new BehaviorSubject<EmpJobMapping[]>(undefined);
  public lastSelectedEmpJobMapping: EmpJobMapping = undefined;
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private empJobMappingDaoService: EmpJobMappingsDaoService,
    private employeesService: EmployeeService,
    private clientStateService: ClientStateService,
    private appParameters: AppParameterService,
    private carsService: CarsService,
  ) {
    this.initialize();

    App.addListener('appStateChange', async (state: AppState) => {
      // state.isActive contains the active state
      if (state.isActive) {
        await this.updateTeamOnNewDate();
      }
    });
  }

  // Public Functions
  public async updateEmpJobMapping(
    empJobMappingsDto: EmpJobMappingDTO[],
  ): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new emp job mapping from other IMS component',
      empJobMappingsDto,
    );

    try {
      let empJobMappings: EmpJobMapping[] = [];

      this.logger.log(
        this.constructor.name,
        'Received EmpJobMappingsDto: ',
        empJobMappingsDto.length,
      );

      let empJobCombinedMappingsDto: EmpJobMappingCombinedDTO[] = [];
      for (const empJobMappingDto of empJobMappingsDto) {
        let found = false;
        for (const empJobCombinedMappingDto of empJobCombinedMappingsDto) {
          if (
            empJobMappingDto.jobid === empJobCombinedMappingDto.jobid &&
            empJobMappingDto.plannedfromts ===
              empJobCombinedMappingDto.plannedfromts &&
            empJobMappingDto.sortvalue === empJobCombinedMappingDto.sortvalue
          ) {
            empJobCombinedMappingDto.empids.push(empJobMappingDto.empid);
            if (empJobMappingDto.isborrowed) {
              empJobCombinedMappingDto.borrowedEmps.push(
                empJobMappingDto.empid,
              );
            }
            if (empJobMappingDto.islent) {
              empJobCombinedMappingDto.lentEmps.push(empJobMappingDto.empid);
            }
            found = true;
          }
        }
        if (!found) {
          empJobCombinedMappingsDto.push({
            jobid: empJobMappingDto.jobid,
            plannedfromts: empJobMappingDto.plannedfromts,
            sortvalue: empJobMappingDto.sortvalue,
            startatflag: empJobMappingDto.startatflag,
            worktypeid: empJobMappingDto.worktypeid,
            comment: empJobMappingDto.comment,
            empids: [empJobMappingDto.empid],
            carid: empJobMappingDto.carid,
            orderitemid: empJobMappingDto.orderitemid,
            borrowedEmps: empJobMappingDto.isborrowed
              ? [empJobMappingDto.empid]
              : [],
            lentEmps: empJobMappingDto.islent ? [empJobMappingDto.empid] : [],
            leader: empJobMappingDto.leader,
          });
        }
      }

      this.logger.log(
        this.constructor.name,
        'Received EmpJobCombinedMappingsDto: ',
        empJobCombinedMappingsDto.length,
        empJobCombinedMappingsDto,
      );

      for (const empJobCombinedMappingDto of empJobCombinedMappingsDto) {
        empJobMappings.push(EmpJobMapping.fromDto(empJobCombinedMappingDto));
      }

      await this.empJobMappingDaoService.saveEmpJobMappings(empJobMappings);
      this.empJobMappings$.next(empJobMappings);

      await this.updateTeamBasedOnEmpJobMappings();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing EmpJobMappings: ',
        error?.message,
      );
      throw error;
    }
  }

  public async updateTeamBasedOnEmpJobMappings() {
    const teamChoser = this.appParameters.appParameters$
      .getValue()?.getBooleanParameter('imsmc.menu.show.teamchooser', false) ?? false;

    this.logger.log(
      this.constructor.name,
      'updateTeamBasedOnEmpJobMappings - teamchooser active=' + teamChoser,
    );
    if (!teamChoser) {
      return;
    }

    const empJobMappings = this.empJobMappings$.getValue() ?? [];

    for (const mapping of empJobMappings) {
      if (mapping.plannedfromts && TimeUtils.isToday(mapping.plannedfromts)) {
        this.logger.log(
          this.constructor.name,
          '- Found a mapping for today',
          mapping,
        );

        let empIds = mapping.empids.map((val) => {
          let empId: IMSId = {
            imsId: val,
            secondaryId: undefined,
            objRef: 'ims/Employee',
          };
          return empId;
        });
        await this.employeesService.updateTeam(empIds);
        await this.empJobMappingDaoService.setLastUpdatedTeam(
          new Date().getTime(),
        );

        if (mapping.leader) {
          this.clientStateService.setCurrentTeamLeader({
            imsId: mapping.leader,
            secondaryId: undefined,
            objRef: 'ims/Employee',
          });
        }

        break;
      }
    }
  }

  public getTodaysPlannedEmployees(): Employee[] {
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    const employees: Employee[] = empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .flatMap((it) => it.empids.map((empId) => this.employeesService.getEmployeeById({
        imsId: empId,
        secondaryId: undefined,
        objRef: 'ims/Employee',
      })))
      .filter((emp) => emp !== undefined) as Employee[];
    const employeesSet = new Set(employees);
    return Array.from(employeesSet);
  }

  public getTodaysPlannedBorrowedEmployees(): Employee[] {
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    const employees: Employee[] = empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .flatMap((it) => it.borrowedEmps.map((empId) => this.employeesService.getEmployeeById({
        imsId: empId,
        secondaryId: undefined,
        objRef: 'ims/Employee',
      })))
      .filter((emp) => emp !== undefined) as Employee[];
    return Array.from(new Set(employees));
  }

  public getTodaysPlannedLentEmployees(): Employee[] {
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    const employees: Employee[] = empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .flatMap((it) => it.lentEmps.map((empId) => this.employeesService.getEmployeeById({
        imsId: empId,
        secondaryId: undefined,
        objRef: 'ims/Employee',
      })))
      .filter((emp) => emp !== undefined) as Employee[];
    return Array.from(new Set(employees));
  }

  public getTodaysPlannedStartAtFlag(): string {
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    return empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .map((it) => it.startatflag)
      .shift();
  }

  public getTodaysStartingPointJobId(): IMSId {
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    return empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .filter((it) => it.jobid != null)
      .filter((it) => it.sortvalue != undefined)
      .sort((a, b) => (a.sortvalue ?? "").localeCompare(b.sortvalue ?? ""))
      .map(it => {
        return it
      })
      .map((it) => it.jobid)
      .shift();
  }


  public getTodaysServiceJobSortValue(id: IMSId): string {
    if (id == undefined) {
      return undefined
    }
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    return empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .filter((it) => IMSObject.equals(it.jobid, id))
      .shift()
      ?.sortvalue
  }

  public getTodaysPlannedCar(): Car {
    const empJobMappings = this.empJobMappings$.getValue() ?? [];
    const carImsId = empJobMappings
      .filter((it) => TimeUtils.isToday(it.plannedfromts))
      .filter((it) => it.carid != undefined)
      .shift()?.carid;

    if (carImsId == undefined) {
      return undefined;
    }
    return this.carsService.getCarById({
      imsId: carImsId,
      secondaryId: undefined,
      objRef: 'ims/Car',
    });
  }

  public getEmpJobMappingForSjobIdAndDay(
    sjobId: IMSId,
    dayTs: number,
  ): EmpJobMapping {
    if (sjobId == undefined || dayTs == undefined) {
      return undefined;
    }

    return this.empJobMappings$
      .getValue()
      .filter((it) => it.jobid.imsId === sjobId.imsId)
      .filter((it) => TimeUtils.isSameDay(dayTs, it.plannedfromts))
      .shift();
  }

  public getUpcomingEmpJobMappingForSjobId(sjobId: IMSId): EmpJobMapping[] {
    if (sjobId == undefined) {
      return [];
    }

    let empJobMappings = this.empJobMappings$.getValue();

    if (empJobMappings == undefined) {
      return [];
    }

    return empJobMappings
      .filter((it) => it.jobid.imsId === sjobId.imsId)
      .filter(
        (it) =>
          TimeUtils.isToday(it.plannedfromts) ||
          TimeUtils.isInTheFuture(it.plannedfromts),
      );
  }

  public async createEmpJobMappingForToday(sjob: Servicejob): Promise<void> {
    const empJobMapping: EmpJobMapping = new EmpJobMapping(
      sjob.id,
      this.employeesService.me$.getValue()
        ? [this.employeesService.me$.getValue().id.imsId]
        : [],
      'D',
      '0000',
      '',
      Date.now(),
      undefined,
      undefined,
      undefined,
      [],
      [],
      undefined,
    );
    const mappings = this.empJobMappings$.getValue();
    mappings.push(empJobMapping);

    await this.empJobMappingDaoService.saveEmpJobMappings(mappings);

    this.empJobMappings$.next(mappings);
  }

  public async removeEmpJobMapping(sjob: Servicejob): Promise<void> {
    const mappings = this.empJobMappings$.getValue();
    let newMapping = mappings.filter((val) => {
      return !IMSObject.equals(val.jobid, sjob.id);
    });

    await this.empJobMappingDaoService.saveEmpJobMappings(newMapping);
    this.empJobMappings$.next(newMapping);
  }

  // Resetable
  public async reset() {
    await this.empJobMappingDaoService.reset();
  }

  public async clear() {
    await this.empJobMappingDaoService.clear();
    this.empJobMappings$.next([]);
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalEmpJobMappings();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {
    this.updateTeamOnNewDate();
  }

  // Private Functions
  private async updateTeamOnNewDate() {
    const today = new Date();
    const lastEdited = new Date(
      await this.empJobMappingDaoService.getLastUpdatedTeam(),
    );
    const isNewDay = !isSameDay(today, lastEdited);
    this.logger.log(
      this.constructor.name,
      'updateTeamOnNewDate - isNewDay=' + isNewDay,
      'today=' + today.toISOString(),
      'lastEdited=' + lastEdited.toISOString(),
    );
    if (!isNewDay) {
      return;
    }

    this.updateTeamBasedOnEmpJobMappings();
  }

  private async loadLocalEmpJobMappings() {
    try {
      this.logger.log(this.constructor.name, '- Load local EmpJobMappings');
      let empJobMappings: EmpJobMapping[] =
        await this.empJobMappingDaoService.getEmpJobMappings();
      this.logger.log(
        this.constructor.name,
        '- Loaded',
        empJobMappings,
        'local EmpJobMappings',
      );

      this.empJobMappings$.next(empJobMappings);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local EmpJobMappings: ',
        error?.message,
      );
      throw error;
    }
  }
}
